import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CandidatePortalShell from './candidate/CandidatePortalShell';
import CandidateDashboardPage from './candidate/pages/CandidateDashboardPage';
import CandidateOpeningsPage from './candidate/pages/CandidateOpeningsPage';
import CandidateApplicationsPage from './candidate/pages/CandidateApplicationsPage';
import CandidateInterviewsPage from './candidate/pages/CandidateInterviewsPage';
import CandidatePracticePage from './candidate/pages/CandidatePracticePage';
import CandidateProfilePage from './candidate/pages/CandidateProfilePage';
import CandidateSettingsPage from './candidate/pages/CandidateSettingsPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import PublicReportPage from './pages/PublicReportPage';
import PublicAptitudeTestPage from './pages/PublicAptitudeTestPage';
import AptitudeTestsPage from './pages/AptitudeTestsPage';
import HostAvailabilityPage from './pages/HostAvailabilityPage';
import CandidateFeedbackPage from './pages/CandidateFeedbackPage';
import CodingInterviewPage from './pages/CodingInterviewPage';
import CodingChallengesPage from './pages/CodingChallengesPage';

import DashboardHome from './pages/DashboardHome';
import InterviewsPage from './pages/InterviewsPage';
import MockInterviewsPage from './pages/MockInterviewsPage';
import ApplicationsPage from './pages/ApplicationsPage';
import CandidatesPage from './pages/CandidatesPage';
import ReportsPage from './pages/ReportsPage';
import BillingPage from './pages/BillingPage';
import SettingsPage from './pages/SettingsPage';
import SessionPage from './pages/SessionPage';
import ReportPage from './pages/ReportPage';
import SchedulesPage from './pages/SchedulesPage';
import OrgPage from './pages/OrgPage';
import PipelinePage from './pages/PipelinePage';
import JobOpeningsPage from './pages/JobOpeningsPage';
import QuestionsPage from './pages/QuestionsPage';
import AllOrgsPage from './pages/admin/AllOrgsPage';
import ProcessSignaturesPage from './pages/admin/ProcessSignaturesPage';
import PendingPaymentsPage from './pages/admin/PendingPaymentsPage';

import AppShell from './components/layout/AppShell';
import ProtectedRoute from './components/ProtectedRoute';
import RecruiterApp from './recruiter/RecruiterApp';
import { authStore } from './api/client';
import { getMe } from './api/auth';
import Spinner from './components/ui/Spinner';

// DEV-only: UI primitives sandbox at /dev/ui (gated by import.meta.env.DEV)
const DevUiSandbox = import.meta.env.DEV
  ? React.lazy(() => import('./pages/DevUiSandbox'))
  : null;

// ---------------------------------------------------------------------------
// RootRedirect
// ---------------------------------------------------------------------------
/**
 * Redirects "/" to /dashboard or /candidate when authenticated, /login otherwise.
 */
function RootRedirect() {
  if (!authStore.isAuthenticated) return <Navigate to="/login" replace />;
  if (authStore.role === 'CANDIDATE') return <Navigate to="/candidate" replace />;
  return <Navigate to="/dashboard" replace />;
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------
/**
 * App
 *
 * Route tree:
 *   PUBLIC:
 *     /login         - LoginPage
 *     /register      - RegisterPage
 *     /unauthorized  - UnauthorizedPage
 *     /candidate     - CandidatePage  (CANDIDATE role only, no shell)
 *
 *   AUTHENTICATED (wrapped in <AppShell />):
 *     /dashboard     - DashboardHome
 *     /interviews    - InterviewsPage   (placeholder)
 *     /candidates    - CandidatesPage   (placeholder)
 *     /reports       - ReportsPage      (placeholder)
 *     /settings      - SettingsPage     (placeholder)
 *     /schedules     - SchedulesPage
 *     /session/:id   - SessionPage
 *     /report/:id    - ReportPage
 *     /orgs          - OrgPage          (ORG_ADMIN, SUPER_ADMIN)
 *
 *   /              - smart redirect
 *   *              - catch-all to /
 */
export default function App() {
  const [booting, setBooting] = React.useState(true);

  // ── On mount: rehydrate tokens from storage and validate via /me.
  // The axios response interceptor will silently refresh on 401 if the
  // access token is expired but the refresh token is still valid.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const hadTokens = authStore.hydrate();
      if (!hadTokens) {
        if (!cancelled) setBooting(false);
        return;
      }
      try {
        const res = await getMe();
        if (cancelled) return;
        // Refresh the cached user (role / profile may have changed server-side)
        authStore.user = res.data;
        // Persist updated user back to storage without changing tokens
        try {
          window.localStorage.setItem(
            'samastix:auth',
            JSON.stringify({
              accessToken: authStore.accessToken,
              refreshToken: authStore.refreshToken,
              user: authStore.user,
            }),
          );
        } catch { /* ignore */ }
      } catch (err) {
        const status = err?.status;
        if (status === 401 || status === 403) {
          console.warn('[App] session invalid, clearing auth:', err?.message);
          authStore.clear();
        } else {
          // Network / server hiccup - keep cached session so the user
          // is not bounced to /login on a transient failure.
          console.warn('[App] /me failed but keeping cached session:', err?.message);
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Spinner size="lg" label="Loading…" />
      </div>
    );
  }

  return (
    <Routes>
      {/* ── Public ───────────────────────────────────────────────────── */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/r/:token" element={<PublicReportPage />} />
      <Route path="/aptitude/:token" element={<PublicAptitudeTestPage />} />
      <Route path="/feedback/:token" element={<CandidateFeedbackPage />} />
      <Route path="/coding/:sessionId" element={<CodingInterviewPage />} />

      {/* ── Recruiter portal (separate dark UI) ─────────────────────── */}
      <Route path="/recruiter/*" element={<RecruiterApp />} />

      <Route
        path="/candidate"
        element={
          <ProtectedRoute allowedRoles={['CANDIDATE']}>
            <CandidatePortalShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<CandidateDashboardPage />} />
        <Route path="openings" element={<CandidateOpeningsPage />} />
        <Route path="applications" element={<CandidateApplicationsPage />} />
        <Route path="interviews" element={<CandidateInterviewsPage />} />
        <Route path="practice" element={<CandidatePracticePage />} />
        <Route path="profile" element={<CandidateProfilePage />} />
        <Route path="settings" element={<CandidateSettingsPage />} />
      </Route>

      {/* ── Authenticated (inside AppShell) ──────────────────────────── */}
      <Route
        element={
          <ProtectedRoute allowedRoles={['INTERVIEWER', 'ORG_ADMIN', 'SUPER_ADMIN']}>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardHome />} />
        <Route path="/interviews" element={<InterviewsPage />} />
        <Route path="/mock-interviews" element={<MockInterviewsPage />} />
        <Route path="/applications" element={<ApplicationsPage />} />
        <Route path="/job-openings" element={<JobOpeningsPage />} />
        <Route path="/pipeline" element={<PipelinePage />} />
        <Route path="/candidates" element={<CandidatesPage />} />
        <Route path="/questions" element={<QuestionsPage />} />
        <Route path="/coding-challenges" element={<CodingChallengesPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/aptitude-tests" element={<AptitudeTestsPage />} />
        <Route path="/settings/availability" element={<HostAvailabilityPage />} />
        <Route path="/schedules" element={<SchedulesPage />} />
        <Route path="/session/:sessionId" element={<SessionPage />} />
        <Route path="/report/:id" element={<ReportPage />} />
        <Route
          path="/orgs"
          element={
            <ProtectedRoute allowedRoles={['ORG_ADMIN', 'SUPER_ADMIN']}>
              <OrgPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/orgs"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <AllOrgsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/signatures"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <ProcessSignaturesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <PendingPaymentsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* ── Smart root + catch-all ────────────────────────────────── */}
      <Route path="/" element={<RootRedirect />} />
      {DevUiSandbox && (
        <Route
          path="/dev/ui"
          element={
            <React.Suspense fallback={null}>
              <DevUiSandbox />
            </React.Suspense>
          }
        />
      )}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
